import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Modal from "react-modal";
import { Carousel } from "react-responsive-carousel";

import Map from "./map";

import "react-responsive-carousel/lib/styles/carousel.min.css";


/* =========================================================
   ICONS
========================================================= */

function LocationPinIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 21C12 21 19 15.5 19 9.5C19 5.36 15.87 2 12 2C8.13 2 5 5.36 5 9.5C5 15.5 12 21 12 21Z"
        fill="currentColor"
      />
      <circle cx="12" cy="9.5" r="2.7" fill="white" />
    </svg>
  );
}


function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="M12 6.7V12L15.8 14.3"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function CarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M5 11l1.5-4.5A2.2 2.2 0 018.6 5h6.8a2.2 2.2 0 012.1 1.5L19 11h1a1 1 0 011 1v6h-2v2h-3v-2H8v2H5v-2H3v-6a1 1 0 011-1h1zm2.1 0h9.8l-1.2-3.6a.7.7 0 00-.7-.5H9a.7.7 0 00-.7.5L7.1 11zM7 15.5A1.5 1.5 0 107 12.5a1.5 1.5 0 000 3zm10 0a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
      />
    </svg>
  );
}


function GiftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 7h-2.2c.15-.32.2-.66.2-1 0-1.65-1.35-3-3-3-1.18 0-2.2.69-3 1.76C11.2 3.69 10.18 3 9 3 7.35 3 6 4.35 6 6c0 .34.06.68.17 1H4a2 2 0 00-2 2v2h9V9h2v2h9V9a2 2 0 00-2-2zM9 7a1 1 0 110-2c.62 0 1.28.77 1.8 2H9zm6 0h-1.8c.52-1.23 1.18-2 1.8-2a1 1 0 110 2zM3 13h8v8H5a2 2 0 01-2-2v-6zm10 0h8v6a2 2 0 01-2 2h-6v-8z"
      />
    </svg>
  );
}


function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18 2H6v2H3v5c0 2.76 2.24 5 5 5h.18A6.02 6.02 0 0011 16.91V19H7v3h10v-3h-4v-2.09A6.02 6.02 0 0015.82 14H16c2.76 0 5-2.24 5-5V4h-3V2zM5 9V6h1v4c0 .59.09 1.16.25 1.7A3 3 0 015 9zm14 0a3 3 0 01-1.25 2.43c.16-.46.25-.94.25-1.43V6h1v3z"
      />
    </svg>
  );
}


/* =========================================================
   FEATURE CARD
========================================================= */

function FeatureCard({ type, title, children }) {
  let icon = null;

  if (type === "parking") icon = <CarIcon />;
  if (type === "location") icon = <LocationPinIcon />;
  if (type === "gift") icon = <GiftIcon />;

  return (
    <div className="location-feature">
      <div className="location-feature-icon">
        {icon}
      </div>

      <h4>{title}</h4>

      <p>{children}</p>
    </div>
  );
}


/* =========================================================
   GALLERY
========================================================= */

function StoreGallery({ images, title }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    Modal.setAppElement("#root");
  }, []);

  const openImage = (index) => {
    setSelectedImage(index);
    setModalOpen(true);
  };

  return (
    <>
      <div className="location-gallery">

        <div className="gallery-heading">

          <span className="gallery-line" />

          <span className="gallery-star">✦</span>

          <h3>STORE GALLERY</h3>

          <span className="gallery-star">✦</span>

          <span className="gallery-line" />

        </div>


        <div className="gallery-grid">

          {images.slice(0, 5).map((image, index) => (
            <button
              key={index}
              type="button"
              className="gallery-image"
              onClick={() => openImage(index)}
            >

              <img
                src={image.src}
                alt={image.alt || `${title} ${index + 1}`}
              />

              <div className="gallery-image-overlay">
                View
              </div>

            </button>
          ))}

        </div>


        {images.length > 5 && (
          <button
            type="button"
            className="view-all-gallery"
            onClick={() => openImage(0)}
          >
            {/* View all {images.length} photos */}
            View all photos
            <span>→</span>
          </button>
        )}

      </div>


      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        className="gallery-modal"
        overlayClassName="gallery-modal-overlay"
      >

        <div className="gallery-modal-header">

          <h2>{title}</h2>

          <button
            type="button"
            className="gallery-modal-close"
            onClick={() => setModalOpen(false)}
          >
            ×
          </button>

        </div>


        <Carousel
          selectedItem={selectedImage}
          onChange={setSelectedImage}
          showThumbs={false}
          showStatus={false}
          infiniteLoop
          useKeyboardArrows
          emulateTouch
        >

          {images.map((image, index) => (
            <div
              className="modal-image-container"
              key={index}
            >
              <img
                src={image.src}
                alt={image.alt || `${title} ${index + 1}`}
              />
            </div>
          ))}

        </Carousel>

      </Modal>
    </>
  );
}


/* =========================================================
   LOCATION PAGE
========================================================= */

function LocationPage({ data }) {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [data]);


  const directionsURL =
    `https://www.google.com/maps/dir/?api=1&destination=${data.coordinates[0]},${data.coordinates[1]}`;


  return (
    <main className="location-page">

      <div className="location-page-container">


        {/* BACK */}

        <div className="location-back-wrapper">

          <Link
            to="/#Stores"
            className="location-back"
          >
            ← Back to All Stores
          </Link>

        </div>


        {/* MAIN CARD */}

        <section className="location-main-card">

          <div className="location-top">


            {/* =================================================
                LEFT SIDE
            ================================================= */}

            <div className="location-info">


              <div className="location-title-row">

                <div className="location-main-pin">
                  <LocationPinIcon />
                </div>

                <h1>{data.titulo}</h1>

              </div>


              <div className="location-pink-divider">
                <span />
                <b>✦</b>
                <span />
              </div>


              <p className="location-address">
                {data.texto}
              </p>


              <div className="location-message">

                <div className="location-message-icon">
                  ♡
                </div>

                <p>
                  <strong>Come play,</strong>{" "}
                  have fun and win awesome prizes for the whole family!
                </p>

              </div>


              {/* HOURS */}

              <div className="opening-hours">

                <div className="opening-hours-heading">

                  <div className="opening-clock">
                    <ClockIcon />
                  </div>

                  <h3>OPENING HOURS</h3>

                </div>


                <div className="opening-hours-list">

                  {data.tablaData.map((day, index) => (
                    <div
                      className="opening-hours-row"
                      key={index}
                    >
                      <span>{day.columna1}</span>
                      <span>{day.columna2}</span>
                    </div>
                  ))}

                </div>

              </div>

            </div>


            {/* =================================================
                RIGHT SIDE
            ================================================= */}

            <div className="location-right">


              {/* MAP
                  THIS WRAPPER NOW FORCES LEAFLET
                  TO STAY INSIDE THE BOX
              */}

              <div className="location-map-frame">

                <div className="location-map-inner">
                  <Map coordinates={data.coordinates} />
                </div>

              </div>


              {/* FEATURES */}

              <div className="location-features">

                <FeatureCard
                  type="parking"
                  title="EASY PARKING"
                >
                  Plenty of parking available on-site.
                </FeatureCard>


                <FeatureCard
                  type="location"
                  title="GREAT LOCATION"
                >
                  {data.texto2 ||
                    "Conveniently located inside the shopping centre."}
                </FeatureCard>


                <FeatureCard
                  type="gift"
                  title="AMAZING PRIZES"
                >
                  Play your favourite claw machines!
                </FeatureCard>

              </div>

            </div>

          </div>


          {/* GALLERY */}

          <StoreGallery
            images={data.images}
            title={data.modalTitle}
          />

        </section>


        {/* =====================================================
            CTA
        ===================================================== */}

        <section className="location-cta">


          <div className="cta-trophy-circle">
            <TrophyIcon />
          </div>


          <div className="cta-divider" />


          <div className="cta-copy">

            <h2>
              <span>Ready</span>{" "}
              to{" "}
              <strong>Play?</strong>
            </h2>

            <p>
              Visit us today and <b>test your skills!</b>
            </p>

          </div>


          <a
            href={directionsURL}
            target="_blank"
            rel="noopener noreferrer"
            className="directions-button"
          >

            <span className="directions-pin">
              <LocationPinIcon />
            </span>

            Get Directions

            <span className="directions-arrow">
              →
            </span>

          </a>


          <span className="cta-sparkle cta-sparkle-one">
            ✦
          </span>

          <span className="cta-sparkle cta-sparkle-two">
            ✦
          </span>


          <div className="cta-wave" />

        </section>


      </div>


      {/* =====================================================
          CSS
      ===================================================== */}

      <style>{`

        /* =====================================================
           RESET
        ===================================================== */

        .location-page,
        .location-page * {
          box-sizing: border-box;
        }


        /* =====================================================
           PAGE
        ===================================================== */

        .location-page {
          width: 100%;
          min-height: 100vh;

          padding: 65px 24px 85px;

          font-family: 'Quicksand', Arial, sans-serif;

          background-image:
            linear-gradient(
              rgba(238,249,252,.22),
              rgba(238,249,252,.22)
            ),
            url('/img/background-stores.jpg');

          background-size: cover;
          background-position: center top;
          background-repeat: repeat-y;
          background-attachment: fixed;

          overflow-x: hidden;
        }


        .location-page-container {
          width: 100%;
          max-width: 1150px;
          margin: 0 auto;
        }


        /* =====================================================
           BACK
        ===================================================== */

        .location-back-wrapper {
          margin-bottom: 18px;
        }


        .location-back {
          display: inline-flex;
          align-items: center;
          gap: 7px;

          padding: 9px 18px;

          background: rgba(255,255,255,.96);

          border-radius: 30px;

          color: #079ed5;

          text-decoration: none;

          font-size: 14px;
          font-weight: 800;

          box-shadow:
            0 5px 15px rgba(28,77,97,.12);

          transition: .25s ease;
        }


        .location-back:hover {
          color: #079ed5;
          transform: translateX(-4px);
        }


        /* =====================================================
           MAIN CARD
        ===================================================== */

        .location-main-card {
          position: relative;

          width: 100%;

          padding: 28px 28px 25px;

          background: rgba(255,255,255,.97);

          border: 2px solid #23c4f4;
          border-radius: 18px;

          box-shadow:
            0 16px 45px rgba(35,80,100,.16);

          overflow: hidden;
        }


        /* =====================================================
           TWO COLUMNS
        ===================================================== */

        .location-top {
          width: 100%;

          display: grid;

          grid-template-columns:
            minmax(320px, .9fr)
            minmax(0, 1.5fr);

          gap: 28px;

          align-items: start;
        }


        /* =====================================================
           LEFT
        ===================================================== */

        .location-info {
          width: 100%;
          min-width: 0;

          padding: 8px 0 0 6px;
        }


        .location-title-row {
          display: flex;
          align-items: center;
          gap: 18px;
        }


        .location-main-pin {
          width: 78px;
          height: 78px;
          min-width: 78px;

          display: flex;
          justify-content: center;
          align-items: center;

          color: #11abe0;

          background: white;

          border: 3px solid #23c4f4;

          border-radius: 50%;

          box-shadow:
            0 4px 12px rgba(0,159,213,.12);
        }


        .location-main-pin svg {
          width: 48px;
          height: 48px;
        }


        .location-title-row h1 {
          margin: 0;

          color: #079ed5;

          font-family:
            'Quicksand-Medium-Bold',
            'Quicksand',
            sans-serif;

          font-size: clamp(29px, 3vw, 44px);
          line-height: 1.07;

          font-weight: 900;
        }


        /* =====================================================
           PINK DIVIDER
        ===================================================== */

        .location-pink-divider {
          display: flex;
          align-items: center;
          gap: 10px;

          margin: 24px 0 22px;

          color: #e93291;
        }


        .location-pink-divider span {
          flex: 1;

          height: 2px;

          background: #e93291;

          opacity: .8;
        }


        /* =====================================================
           ADDRESS
        ===================================================== */

        .location-address {
          max-width: 360px;

          margin: 0 auto 22px;

          color: #222832;

          text-align: center;

          font-size: 15px;
          font-weight: 700;
          line-height: 1.5;
        }


        /* =====================================================
           MESSAGE
        ===================================================== */

        .location-message {
          display: flex;
          align-items: center;
          gap: 15px;

          padding: 14px 17px;

          margin-bottom: 30px;

          background:
            linear-gradient(
              90deg,
              #eff9fe,
              #e5f5fc
            );

          border-radius: 10px;
        }


        .location-message-icon {
          width: 38px;
          height: 38px;
          min-width: 38px;

          display: flex;
          align-items: center;
          justify-content: center;

          color: #11abe0;

          border: 2px solid #11abe0;
          border-radius: 8px;

          font-size: 20px;
          font-weight: 900;
        }


        .location-message p {
          margin: 0;

          color: #252b34;

          font-size: 13.5px;
          line-height: 1.45;

          font-weight: 600;
        }


        .location-message strong {
          color: #079ed5;
          font-weight: 900;
        }


        /* =====================================================
           OPENING HOURS
        ===================================================== */

        .opening-hours {
          width: 100%;
        }


        .opening-hours-heading {
          display: flex;
          align-items: center;
          gap: 10px;

          margin-bottom: 10px;
        }


        .opening-clock {
          width: 30px;
          height: 30px;

          color: #079ed5;
        }


        .opening-clock svg {
          width: 100%;
          height: 100%;
        }


        .opening-hours-heading h3 {
          margin: 0;

          color: #078fc7;

          font-size: 18px;
          font-weight: 900;
        }


        .opening-hours-row {
          display: grid;

          grid-template-columns: 1fr auto;

          gap: 20px;

          padding: 5px 2px;

          color: #262b33;

          border-bottom: 1px dotted #b9dbe7;

          font-size: 13.5px;
        }


        .opening-hours-row:last-child {
          border-bottom: none;
        }


        /* =====================================================
           RIGHT COLUMN
        ===================================================== */

        .location-right {
          width: 100%;
          min-width: 0;

          display: flex;
          flex-direction: column;
        }


        /* =====================================================
           MAP — IMPORTANT FIX
        ===================================================== */

        .location-map-frame {
          position: relative !important;

          width: 100% !important;
          height: 390px !important;
          min-height: 390px !important;

          overflow: hidden !important;

          border-radius: 12px;

          background: #e7e7e7;

          isolation: isolate;
        }


        .location-map-inner {
          position: absolute !important;

          inset: 0 !important;

          width: 100% !important;
          height: 100% !important;

          overflow: hidden !important;

          border-radius: inherit;
        }


        /*
         * FORCE ANY WRAPPER INSIDE MAP.JS
         * TO TAKE ONLY THIS AVAILABLE SPACE
         */

        .location-map-inner > div {
          position: relative !important;

          top: auto !important;
          right: auto !important;
          bottom: auto !important;
          left: auto !important;

          width: 100% !important;
          max-width: 100% !important;

          height: 100% !important;
          min-height: 100% !important;
          max-height: 100% !important;

          margin: 0 !important;

          overflow: hidden !important;
        }


        /*
         * OVERRIDE OLD .map-container RULES
         */

        .location-map-inner .map-container {
          position: relative !important;

          width: 100% !important;
          max-width: 100% !important;

          height: 100% !important;
          min-height: 100% !important;
          max-height: 100% !important;

          margin: 0 !important;

          overflow: hidden !important;

          border-radius: 12px;
        }


        /*
         * OVERRIDE OLD GLOBAL LEAFLET CSS
         */

        .location-map-inner .leaflet-container {
          position: relative !important;

          top: auto !important;
          left: auto !important;

          width: 100% !important;
          max-width: 100% !important;

          height: 100% !important;
          min-height: 390px !important;
          max-height: 390px !important;

          margin: 0 !important;

          overflow: hidden !important;

          border-radius: 12px;

          z-index: 1 !important;
        }


        /*
         * LEAFLET INTERNAL ELEMENTS
         */

        .location-map-inner .leaflet-map-pane,
        .location-map-inner .leaflet-tile-pane,
        .location-map-inner .leaflet-overlay-pane,
        .location-map-inner .leaflet-marker-pane,
        .location-map-inner .leaflet-shadow-pane,
        .location-map-inner .leaflet-popup-pane {
          position: absolute;
        }


        .location-map-inner .leaflet-control-container {
          position: relative;
          z-index: 5;
        }


        /* =====================================================
           FEATURE CARDS
        ===================================================== */

        .location-features {
          width: 100%;

          display: grid;

          grid-template-columns:
            repeat(3, minmax(0,1fr));

          gap: 13px;

          margin-top: 12px;

          padding: 14px;

          background: #f4fbfe;

          border-radius: 12px;
        }


        .location-feature {
          min-width: 0;
          min-height: 165px;

          padding: 18px 13px;

          display: flex;
          flex-direction: column;

          align-items: center;
          justify-content: center;

          text-align: center;

          background: white;

          border-radius: 10px;

          box-shadow:
            0 4px 13px rgba(25,70,90,.11);
        }


        .location-feature-icon {
          width: 47px;
          height: 47px;

          margin-bottom: 10px;

          color: #079ed5;
        }


        .location-feature-icon svg {
          width: 100%;
          height: 100%;
        }


        .location-feature h4 {
          margin: 0 0 8px;

          color: #181c22;

          font-size: 14px;
          font-weight: 900;
        }


        .location-feature p {
          margin: 0;

          color: #555d65;

          font-size: 12px;
          line-height: 1.4;
        }


        /* =====================================================
           GALLERY
        ===================================================== */

        .location-gallery {
          width: 100%;

          margin-top: 32px;
        }


        .gallery-heading {
          display: flex;
          align-items: center;
          justify-content: center;

          gap: 12px;

          margin-bottom: 20px;
        }


        .gallery-line {
          flex: 1;

          height: 1.5px;

          background: #23c4f4;
        }


        .gallery-star {
          color: #e93291;
          font-size: 15px;
        }


        .gallery-heading h3 {
          margin: 0;

          color: #078fc7;

          font-size: 18px;
          font-weight: 900;

          white-space: nowrap;
        }


        .gallery-grid {
          display: grid;

          grid-template-columns:
            repeat(5, minmax(0,1fr));

          gap: 14px;
        }


        .gallery-image {
          position: relative;

          width: 100%;
          height: 125px;

          padding: 0;

          overflow: hidden;

          background: #ddd;

          border: none;
          border-radius: 9px;

          cursor: pointer;
        }


        .gallery-image img {
          display: block;

          width: 100%;
          height: 100%;

          object-fit: cover;

          transition: transform .35s ease;
        }


        .gallery-image-overlay {
          position: absolute;

          inset: 0;

          display: flex;
          justify-content: center;
          align-items: center;

          color: white;

          background: rgba(0,144,200,.54);

          opacity: 0;

          font-weight: 800;

          transition: opacity .3s ease;
        }


        .gallery-image:hover img {
          transform: scale(1.07);
        }


        .gallery-image:hover
        .gallery-image-overlay {
          opacity: 1;
        }


        .view-all-gallery {
          display: block;

          margin: 18px auto 0;

          padding: 9px 18px;

          color: #079ed5;

          background: white;

          border: 1.5px solid #23c4f4;
          border-radius: 25px;

          font-family: 'Quicksand', sans-serif;
          font-weight: 800;
          font-size: 13px;

          cursor: pointer;
        }


        .view-all-gallery:hover {
          color: white;
          background: #23c4f4;
        }


        .view-all-gallery span {
          margin-left: 8px;
        }


        /* =====================================================
           MODAL
        ===================================================== */

        .gallery-modal-overlay {
          position: fixed;

          inset: 0;

          z-index: 9999;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 20px;

          background: rgba(10,15,20,.88);
        }


        .gallery-modal {
          position: relative;

          width: min(100%,1000px);

          max-height: 92vh;

          padding: 18px;

          overflow: auto;

          background: white;

          border-radius: 16px;

          outline: none;
        }


        .gallery-modal-header {
          display: flex;

          justify-content: space-between;
          align-items: center;

          gap: 20px;

          margin-bottom: 15px;
        }


        .gallery-modal-header h2 {
          margin: 0;

          color: #078fc7;

          font-size: 22px;
          font-weight: 900;
        }


        .gallery-modal-close {
          width: 40px;
          height: 40px;

          display: flex;
          align-items: center;
          justify-content: center;

          color: white;

          background: #e93291;

          border: none;
          border-radius: 50%;

          font-size: 27px;

          cursor: pointer;
        }


        .modal-image-container {
          height: 650px;

          display: flex;
          align-items: center;
          justify-content: center;

          background: #f2f2f2;
        }


        .modal-image-container img {
          width: 100% !important;
          height: 100%;

          object-fit: contain;
        }


        /* =====================================================
           CTA
        ===================================================== */

        .location-cta {
          position: relative;

          width: 100%;
          min-height: 165px;

          margin-top: 32px;

          padding: 30px 65px 36px;

          display: grid;

          grid-template-columns:
            90px 2px 1fr auto;

          gap: 28px;

          align-items: center;

          overflow: hidden;

          background: rgba(255,255,255,.97);

          border: 1.5px solid #7cd8f5;
          border-radius: 20px;

          box-shadow:
            0 12px 35px rgba(35,80,100,.16);
        }


        .cta-trophy-circle {
          position: relative;
          z-index: 3;

          width: 78px;
          height: 78px;

          display: flex;
          justify-content: center;
          align-items: center;

          color: #e93291;

          background: white;

          border: 1.5px dashed #6f82a7;
          border-radius: 50%;

          box-shadow:
            0 5px 13px rgba(28,68,85,.14);
        }


        .cta-trophy-circle svg {
          width: 44px;
          height: 44px;
        }


        .cta-divider {
          position: relative;
          z-index: 3;

          width: 2px;
          height: 70px;

          background: #23c4f4;
        }


        .cta-copy {
          position: relative;
          z-index: 3;
        }


        .cta-copy h2 {
          margin: 0 0 6px;

          color: #674c85;

          font-size: 34px;
          font-weight: 900;
        }


        .cta-copy h2 span {
          color: #079ed5;
        }


        .cta-copy h2 strong {
          color: #e93291;
        }


        .cta-copy p {
          margin: 0;

          color: #29313a;

          font-size: 15px;
        }


        .directions-button {
          position: relative;
          z-index: 3;

          min-width: 250px;
          min-height: 54px;

          padding: 0 25px;

          display: flex;
          align-items: center;
          justify-content: center;

          gap: 14px;

          color: white;

          text-decoration: none;

          background:
            linear-gradient(
              135deg,
              #079ed5,
              #12b7e8
            );

          border-radius: 32px;

          box-shadow:
            0 7px 15px rgba(0,155,210,.25);

          font-size: 16px;
          font-weight: 900;

          transition: .25s ease;
        }


        .directions-button:hover {
          color: white;

          transform: translateY(-3px);
        }


        .directions-pin {
          width: 25px;
          height: 25px;
        }


        .directions-pin svg {
          width: 100%;
          height: 100%;
        }


        .directions-arrow {
          font-size: 21px;

          transition: transform .25s ease;
        }


        .directions-button:hover
        .directions-arrow {
          transform: translateX(5px);
        }


        .cta-sparkle {
          position: absolute;

          z-index: 2;

          color: #beeafa;
        }


        .cta-sparkle-one {
          left: 20px;
          bottom: 25px;

          font-size: 23px;
        }


        .cta-sparkle-two {
          right: 28px;
          top: 24px;

          font-size: 20px;
        }


        .cta-wave {
          position: absolute;

          z-index: 1;

          left: -5%;
          right: -5%;

          bottom: -56px;

          height: 90px;

          background: #edf8fe;

          border-radius: 50% 50% 0 0;

          transform: rotate(-2deg);
        }


        /* =====================================================
           TABLET
        ===================================================== */

        @media (max-width: 950px) {

          .location-page {
            padding: 50px 18px 70px;
          }


          .location-top {
            grid-template-columns: 1fr;
          }


          .location-map-frame {
            height: 360px !important;
            min-height: 360px !important;
          }


          .location-map-inner .leaflet-container {
            min-height: 360px !important;
            max-height: 360px !important;
          }


          .gallery-grid {
            grid-template-columns:
              repeat(3, minmax(0,1fr));
          }


          .gallery-image {
            height: 155px;
          }


          .location-cta {
            grid-template-columns:
              75px 2px 1fr;
          }


          .directions-button {
            grid-column: 1 / -1;

            width: min(100%,350px);

            margin: 5px auto 0;
          }

        }


        /* =====================================================
           MOBILE
        ===================================================== */

        @media (max-width: 650px) {

          .location-page {
            padding: 35px 12px 55px;

            background-attachment: scroll;
          }


          .location-main-card {
            padding: 17px 14px 18px;

            border-radius: 14px;
          }


          .location-title-row {
            flex-direction: column;

            text-align: center;
          }


          .location-title-row h1 {
            font-size: 31px;
          }


          .location-main-pin {
            width: 65px;
            height: 65px;
            min-width: 65px;
          }


          .location-main-pin svg {
            width: 40px;
            height: 40px;
          }


          .location-map-frame {
            height: 280px !important;
            min-height: 280px !important;
          }


          .location-map-inner .leaflet-container {
            min-height: 280px !important;
            max-height: 280px !important;
          }


          .location-features {
            grid-template-columns: 1fr;
          }


          .location-feature {
            min-height: 125px;
          }


          .gallery-grid {
            grid-template-columns:
              repeat(2, minmax(0,1fr));

            gap: 9px;
          }


          .gallery-image {
            height: 130px;
          }


          .location-cta {
            min-height: auto;

            grid-template-columns: 1fr;

            justify-items: center;

            gap: 15px;

            padding: 28px 20px 40px;

            text-align: center;
          }


          .cta-divider {
            width: 70px;
            height: 2px;
          }


          .cta-copy h2 {
            font-size: 29px;
          }


          .directions-button {
            grid-column: auto;

            width: 100%;
            min-width: 0;
          }


          .modal-image-container {
            height: 65vh;
          }

        }

      `}</style>

    </main>
  );
}


/* =========================================================
   STORE DATA
========================================================= */

const Compo1 = {
  titulo: "Ormiston Town Centre",

  texto:
    "Kranestation is located in 240 Ormiston Road, Flat Bush, Auckland 2016",

  texto2:
    "Located in the Ormiston Town Centre Food Court by the HOYTS escalators.",

  coordinates:
    [-36.9661577, 174.9126717],

  tablaData: [
    { columna1: "Monday", columna2: "10:00am - 22:00pm" },
    { columna1: "Tuesday", columna2: "10:00am - 22:00pm" },
    { columna1: "Wednesday", columna2: "10:00am - 22:00pm" },
    { columna1: "Thursday", columna2: "10:00am - 22:00pm" },
    { columna1: "Friday", columna2: "10:00am - 22:00pm" },
    { columna1: "Saturday", columna2: "10:00am - 22:00pm" },
    { columna1: "Sunday", columna2: "10:00am - 22:00pm" },
  ],

  images: Array.from(
    { length: 30 },
    (_, index) => ({
      src: `/img/Ormiston/img${index + 1}.jpg`,
      alt: "play toy crane",
    })
  ),

  modalTitle:
    "Ormiston Town Centre Gallery",
};


const Compo2 = {
  titulo:
    "Sylvia Park - HOYTS Cinemas",

  texto:
    "Kranestation is located in Sylvia Park Shopping Centre, 286 Mount Wellington Highway, 1060",

  texto2:
    "Located inside Sylvia Park Shopping Centre.",

  coordinates:
    [-36.9148183, 174.8417822],

  tablaData: [
    { columna1: "Monday", columna2: "09:45am - 11:30pm" },
    { columna1: "Tuesday", columna2: "09:45am - 11:30pm" },
    { columna1: "Wednesday", columna2: "09:45am - 11:30pm" },
    { columna1: "Thursday", columna2: "09:45am - 11:30pm" },
    { columna1: "Friday", columna2: "09:45am - 11:30pm" },
    { columna1: "Saturday", columna2: "09:45am - 11:30pm" },
    { columna1: "Sunday", columna2: "09:45am - 11:30pm" },
  ],

  images: Array.from(
    { length: 30 },
    (_, index) => ({
      src: `/img/Hoyts/img${index + 1}.jpg`,
      alt: "play toy crane",
    })
  ),

  modalTitle:
    "Sylvia Park HOYTS Cinemas Gallery",
};


const Compo3 = {
  titulo:
    "Lynn Mall",

  texto:
    "3058 Great North Road, New Lynn, Auckland 0640",

  texto2:
    "Located inside LynnMall Shopping Centre.",

  coordinates:
    [-36.9076227, 174.6859924],

  tablaData: [
    { columna1: "Monday", columna2: "9:00am - 21:00pm" },
    { columna1: "Tuesday", columna2: "9:00am - 21:00pm" },
    { columna1: "Wednesday", columna2: "9:00am - 21:00pm" },
    { columna1: "Thursday", columna2: "9:00am - 21:00pm" },
    { columna1: "Friday", columna2: "9:00am - 21:00pm" },
    { columna1: "Saturday", columna2: "9:00am - 21:00pm" },
    { columna1: "Sunday", columna2: "9:00am - 21:00pm" },
  ],

  images: Array.from(
    { length: 10 },
    (_, index) => ({
      src:
        `/img/lynnMall/img${String(index + 1).padStart(2, "0")}.jpg`,
      alt: "play toy crane",
    })
  ),

  modalTitle:
    "LynnMall Shopping Centre",
};


const Compo4 = {
  titulo:
    "Dress Smart",

  texto:
    "51 Arthur Street, Onehunga, Auckland 0632",

  texto2:
    "Located inside Dress Smart Outlet Shopping Centre.",

  coordinates:
    [-36.9217166, 174.7866633],

  tablaData: [
    { columna1: "Monday", columna2: "10:00am - 18:00pm" },
    { columna1: "Tuesday", columna2: "10:00am - 18:00pm" },
    { columna1: "Wednesday", columna2: "10:00am - 18:00pm" },
    { columna1: "Thursday", columna2: "10:00am - 18:00pm" },
    { columna1: "Friday", columna2: "10:00am - 18:00pm" },
    { columna1: "Saturday", columna2: "9:00am - 18:00pm" },
    { columna1: "Sunday", columna2: "9:00am - 18:00pm" },
  ],

  images: Array.from(
    { length: 7 },
    (_, index) => ({
      src:
        `/img/DressSmart/img${String(index + 1).padStart(2, "0")}.jpg`,
      alt: "claw machine",
    })
  ),

  modalTitle:
    "Dress Smart Outlet Shopping Centre",
};


const Compo5 = {
  titulo:
    "Shore City",

  texto:
    "52/56 Anzac Street, Takapuna, Auckland 0622",

  texto2:
    "Located inside Shore City Shopping Centre.",

  coordinates:
    [-36.787808, 174.7699617],

  tablaData: [
    { columna1: "Monday", columna2: "9:00am - 18:00pm" },
    { columna1: "Tuesday", columna2: "9:00am - 18:00pm" },
    { columna1: "Wednesday", columna2: "9:00am - 18:00pm" },
    { columna1: "Thursday", columna2: "9:00am - 21:00pm" },
    { columna1: "Friday", columna2: "9:00am - 18:00pm" },
    { columna1: "Saturday", columna2: "9:00am - 18:00pm" },
    { columna1: "Sunday", columna2: "9:00am - 17:00pm" },
  ],

  images: [
    {
      src: "/img/ShoreCity/img01.jpg",
      alt: "claw machine",
    },
    {
      src: "/img/ShoreCity/img02.jpg",
      alt: "claw machine",
    },
  ],

  modalTitle:
    "Shore City Shopping Centre",
};


const Compo6 = {
  titulo:
    "Aotea Square",

  texto:
    "Aotea Square, Auckland City 1010",

  texto2:
    "Located in Auckland City.",

  coordinates:
    [-36.849435, 174.7699988],

  tablaData: [
    { columna1: "Monday", columna2: "7:00am - 10:00pm" },
    { columna1: "Tuesday", columna2: "7:00am - 10:00pm" },
    { columna1: "Wednesday", columna2: "7:00am - 10:00pm" },
  ],

  images: [
    {
      src: "/img/AoteaSquare/img1.jpg",
      alt: "claw machine",
    },
    {
      src: "/img/AoteaSquare/img2.jpg",
      alt: "claw machine",
    },
    {
      src: "/img/AoteaSquare/img3.jpg",
      alt: "claw machine",
    },
    {
      src: "/img/AoteaSquare/img4.jpg",
      alt: "claw machine",
    },
  ],

  modalTitle:
    "Aotea Square Gallery",
};


/* =========================================================
   ROUTER
========================================================= */

function TableComponent() {

  const { id } = useParams();


  const components = {
    "1": Compo1,
    "2": Compo2,
    "3": Compo3,
    "4": Compo4,
    "5": Compo5,
    "6": Compo6,
  };


  const selectedComponent =
    components[id] || Compo1;


  return (
    <LocationPage
      data={selectedComponent}
    />
  );
}


export default TableComponent;